import React, { useEffect, useState } from "react";
import axios from "../axios";
import { useNavigate } from "react-router-dom";
import "./GstSummary.css";

const API_URL = process.env.REACT_APP_API_URL;

const GstSummary = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [traderSummary, setTraderSummary] = useState([]);
  const [gstrSummary, setGstrSummary] = useState([]); 
  const [revenueAnalysis, setRevenueAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Filter states for Payment Pending section
  const [paymentPendingSearch, setPaymentPendingSearch] = useState("");
  const [paymentPendingGstrFilter, setPaymentPendingGstrFilter] = useState("");

  useEffect(() => {
    fetchSummaryData();
  }, []);

  const fetchSummaryData = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [dashboardRes, traderRes, gstrRes, revenueRes] = await Promise.all([
        axios.get(`${API_URL}api/gst-summary/executive_dashboard/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}api/gst-summary/trader_summary/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}api/gst-summary/gstr_wise_summary/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}api/gst-summary/revenue_analysis/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setDashboardData(dashboardRes.data);
      setTraderSummary(traderRes.data.trader_summary || []);
      setGstrSummary(gstrRes.data.gstr_summary || []);
      setRevenueAnalysis(revenueRes.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
      setError(error.response?.data?.detail || "Failed to load summary data");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Use ACTUAL backend data - no calculation needed
  const getEnhancedTraderData = (trader) => {
    return {
      ...trader,
      // Backend now provides these directly
      collectedFees: trader.collected_fees || 0,
      uncollectedFees: trader.uncollected_fees || 0,
    };
  };

  // Filter traders with issues
  const getProblematicTraders = () => {
    return traderSummary
      .filter(trader => 
        (trader.not_filed_count > 0) || 
        (trader.payments_pending > 0)
      )
      .map(getEnhancedTraderData)
      .sort((a, b) => b.uncollectedFees - a.uncollectedFees);
  };

  // Get traders with not filed returns
  const getNotFiledTraders = () => {
    return traderSummary
      .filter(trader => trader.not_filed_count > 0)
      .map(getEnhancedTraderData)
      .sort((a, b) => b.not_filed_count - a.not_filed_count);
  };

  // Get traders with payment pending - SORTED BY UNCOLLECTED FEES
  const getPaymentPendingTraders = () => {
    let filtered = traderSummary
      .filter(trader => trader.payments_pending > 0)
      .map(getEnhancedTraderData);

    // Apply search filter
    if (paymentPendingSearch) {
      const searchLower = paymentPendingSearch.toLowerCase();
      filtered = filtered.filter(trader =>
        (trader.trade_name__trade_name || "").toLowerCase().includes(searchLower)
      );
    }

    // Apply GSTR type filter
    if (paymentPendingGstrFilter) {
      // Filter traders who have the selected GSTR type with pending payments
      // We need to check the original records for this
      const tradersWithGstr = new Set();
      
      // This is a simplified approach - you might need to enhance based on your data structure
      filtered = filtered.filter(trader => {
        // You may need to add gstr_types array to the trader_summary endpoint
        // For now, we'll show all if GSTR filter is selected
        // TODO: Enhance backend to include gstr_types per trader
        return true;
      });
    }

    return filtered.sort((a, b) => b.uncollectedFees - a.uncollectedFees);
  };

  // Get all traders sorted by uncollected fees
  const getAllTradersSortedByUncollected = () => {
    return traderSummary
      .map(getEnhancedTraderData)
      .sort((a, b) => b.uncollectedFees - a.uncollectedFees);
  };

  // Calculate revenue at risk (estimated)
  const getRevenueLoss = () => {
    if (!dashboardData?.payment_stats) return 0;
    return dashboardData.payment_stats.revenue_pending || 0;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳ Loading summary data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-box">
          <div className="error-icon">❌</div>
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const problematicTraders = getProblematicTraders();
  const notFiledTraders = getNotFiledTraders();
  const paymentPendingTraders = getPaymentPendingTraders();
  const allTradersSorted = getAllTradersSortedByUncollected();
  const revenueLoss = getRevenueLoss();

  return (
    <div className="gst-summary-container">
      {/* Header */}
      <div className="summary-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          ← Back
        </button>
        <h1>📊 GST Filing Summary & Staff Accountability</h1>
        <button onClick={fetchSummaryData} className="refresh-btn">
          🔄
        </button>
      </div>

      {/* Alert Section - Issues Overview */}
      <div className="alert-section">
        <div className="alert-card critical">
          <div className="alert-icon">⚠️</div>
          <div className="alert-content">
            <h3>Issues Found</h3>
            <div className="alert-value">{problematicTraders.length}</div>
            <p>Traders need attention</p>
          </div>
        </div>

        <div className="alert-card warning">
          <div className="alert-icon">📝</div>
          <div className="alert-content">
            <h3>Not Filed Returns</h3>
            <div className="alert-value">{notFiledTraders.length}</div>
            <p>Pending GST returns</p>
          </div>
        </div>

        <div className="alert-card info">
          <div className="alert-icon">💰</div>
          <div className="alert-content">
            <h3>Payment Pending</h3>
            <div className="alert-value">{paymentPendingTraders.length}</div>
            <p>Fees not collected</p>
          </div>
        </div>

        <div className="alert-card danger">
          <div className="alert-icon">💸</div>
          <div className="alert-content">
            <h3>Revenue at Risk</h3>
            <div className="alert-value">₹{revenueLoss.toLocaleString()}</div>
            <p>Uncollected fees</p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {dashboardData && (
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-icon">📄</div>
            <div className="kpi-label">Total Filings</div>
            <div className="kpi-value">{dashboardData.kpis.total_filings}</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">💰</div>
            <div className="kpi-label">Total Revenue</div>
            <div className="kpi-value">
              ₹{(dashboardData.kpis.total_revenue || 0).toLocaleString()}
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">📊</div>
            <div className="kpi-label">Average Revenue</div>
            <div className="kpi-value">
              ₹{(dashboardData.kpis.avg_revenue_per_filing || 0).toFixed(2)}
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">✅</div>
            <div className="kpi-label">Compliance Rate</div>
            <div className="kpi-value">
              {(dashboardData.kpis.compliance_rate || 0).toFixed(1)}%
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">💳</div>
            <div className="kpi-label">Payment Collection</div>
            <div className="kpi-value">
              {(dashboardData.kpis.payment_collection_rate || 0).toFixed(1)}%
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">👥</div>
            <div className="kpi-label">Active Traders</div>
            <div className="kpi-value">{dashboardData.active_traders || 0}</div>
          </div>
        </div>
      )}

      {/* Payment Status Overview */}
      {dashboardData?.payment_stats && (
        <div className="payment-overview">
          <h2>💳 Payment Status Overview</h2>
          <div className="payment-stats-grid">
            <div className="payment-stat success">
              <h3>✓ Payments Received</h3>
              <div className="stat-count">{dashboardData.payment_stats.received_count}</div>
              <div className="stat-amount">
                ₹{(dashboardData.payment_stats.revenue_received || 0).toLocaleString()}
              </div>
            </div>
            <div className="payment-stat pending">
              <h3>⏳ Payments Pending</h3>
              <div className="stat-count">{dashboardData.payment_stats.pending_count}</div>
              <div className="stat-amount">
                ₹{(dashboardData.payment_stats.revenue_pending || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ CRITICAL: Not Filed Returns */}
      {notFiledTraders.length > 0 && (
        <div className="critical-section">
          <div className="section-header">
            <h2>⚠️ Critical: Returns Not Filed</h2>
            <span className="badge danger">{notFiledTraders.length} Traders</span>
          </div>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Trade Name</th>
                  <th>Total Filings</th>
                  <th>Not Filed</th>
                  <th>Filed</th>
                  <th>Total Fees</th>
                  <th>Collected Fees</th>
                  <th>Uncollected Fees</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {notFiledTraders.map((trader, index) => (
                  <tr key={index} className="critical-row">
                    <td>{index + 1}</td>
                    <td className="trader-name">{trader.trade_name__trade_name}</td>
                    <td>{trader.total_filings}</td>
                    <td className="highlight-danger">{trader.not_filed_count}</td>
                    <td>{trader.filed_count || 0}</td>
                    <td>₹{(trader.total_fees || 0).toLocaleString()}</td>
                    <td className="highlight-success">₹{(trader.collectedFees || 0).toLocaleString()}</td>
                    <td className="highlight-danger">₹{(trader.uncollectedFees || 0).toLocaleString()}</td>
                    <td>
                      <span className="status-badge critical">Action Required</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 💰 CRITICAL: Payment Pending - SORTED BY UNCOLLECTED FEES */}
      {paymentPendingTraders.length > 0 && (
        <div className="critical-section">
          <div className="section-header">
            <h2>💰 Critical: Payments Not Received</h2>
            <span className="badge warning">{paymentPendingTraders.length} Traders</span>
            <span className="badge info">Sorted by Uncollected Fees ↓</span>
          </div>

          {/* Search and Filter Controls */}
          <div className="section-filters">
            <div className="filter-row">
              <div className="filter-input-group">
                <label className="filter-label">
                  <span className="filter-icon">🔍</span>
                  Search Trader
                </label>
                <input
                  type="text"
                  placeholder="Search by trade name..."
                  value={paymentPendingSearch}
                  onChange={(e) => setPaymentPendingSearch(e.target.value)}
                  className="search-input"
                />
                {paymentPendingSearch && (
                  <button
                    onClick={() => setPaymentPendingSearch("")}
                    className="clear-search-btn"
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="filter-input-group">
                <label className="filter-label">
                  <span className="filter-icon">📋</span>
                  GSTR Type
                </label>
                <select
                  value={paymentPendingGstrFilter}
                  onChange={(e) => setPaymentPendingGstrFilter(e.target.value)}
                  className="filter-select-input"
                >
                  <option value="">All GSTR Types</option>
                  <option value="gstr1">GSTR-1</option>
                  <option value="gstr3b">GSTR-3B</option>
                  <option value="gstr9">GSTR-9</option>
                  <option value="gstr9c">GSTR-9C</option>
                  <option value="cmp08">CMP-08</option>
                </select>
              </div>

              {(paymentPendingSearch || paymentPendingGstrFilter) && (
                <button
                  onClick={() => {
                    setPaymentPendingSearch("");
                    setPaymentPendingGstrFilter("");
                  }}
                  className="clear-all-filters-btn"
                >
                  🔄 Clear Filters
                </button>
              )}
            </div>

            <div className="filter-results-info">
              Showing {paymentPendingTraders.length} trader(s) with pending payments
              {(paymentPendingSearch || paymentPendingGstrFilter) && " (filtered)"}
            </div>
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Trade Name</th>
                  <th>Total Filings</th>
                  <th>Paid</th>
                  <th>Unpaid</th>
                  <th>Total Fees</th>
                  <th>Collected Fees</th>
                  <th className="highlight-column">Uncollected Fees ↓</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentPendingTraders.length > 0 ? (
                  paymentPendingTraders.map((trader, index) => (
                    <tr key={index} className="warning-row">
                      <td>{index + 1}</td>
                      <td className="trader-name">{trader.trade_name__trade_name}</td>
                      <td>{trader.total_filings}</td>
                      <td className="highlight-success">{trader.payments_received || 0}</td>
                      <td className="highlight-danger">{trader.payments_pending}</td>
                      <td>₹{(trader.total_fees || 0).toLocaleString()}</td>
                      <td className="highlight-success">₹{(trader.collectedFees || 0).toLocaleString()}</td>
                      <td className="highlight-danger highlight-column">
                        <strong>₹{(trader.uncollectedFees || 0).toLocaleString()}</strong>
                      </td>
                      <td>
                        <span className="status-badge warning">Follow Up Required</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="no-results">
                      <div className="empty-state">
                        <span className="empty-icon">🔍</span>
                        <p>No traders found matching your filters</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Performers */}
      <div className="top-performers">
        <div className="section-header">
          <h2>🏆 Top 10 Traders by Revenue</h2>
          <span className="badge success">Best Performers</span>
        </div>
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Trade Name</th>
                <th>Total Filings</th>
                <th>Filed</th>
                <th>Not Filed</th>
                <th>Total Fees</th>
                <th>Collected Fees</th>
                <th>Uncollected Fees</th>
                <th>Avg Fees</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {allTradersSorted.slice(0, 10).map((trader, index) => (
                <tr key={index}>
                  <td>
                    {index < 3 ? (
                      <span className="medal">{['🥇', '🥈', '🥉'][index]}</span>
                    ) : (
                      index + 1
                    )}
                  </td>
                  <td className="trader-name">{trader.trade_name__trade_name}</td>
                  <td>{trader.total_filings}</td>
                  <td>{trader.filed_count || 0}</td>
                  <td>{trader.not_filed_count || 0}</td>
                  <td>₹{(trader.total_fees || 0).toLocaleString()}</td>
                  <td className="highlight-success">₹{(trader.collectedFees || 0).toLocaleString()}</td>
                  <td className="highlight-danger">₹{(trader.uncollectedFees || 0).toLocaleString()}</td>
                  <td>₹{(trader.avg_fees || 0).toFixed(2)}</td>
                  <td>
                    <div className="payment-progress">
                      {trader.payments_received || 0}/{trader.total_filings}
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${trader.total_filings > 0 ? ((trader.payments_received || 0) / trader.total_filings) * 100 : 0}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* GSTR Type Summary */}
      <div className="gstr-summary">
        <h2>📑 GSTR Type Summary</h2>
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>GSTR Type</th>
                <th>Total Filings</th>
                <th>Filed</th>
                <th>Not Filed</th>
                <th>Paid</th>
                <th>Unpaid</th>
                <th>Total Fees</th>
                <th>Collected Fees</th>
                <th>Uncollected Fees</th>
              </tr>
            </thead>
            <tbody>
              {gstrSummary.map((gstr) => (
                <tr key={gstr.gstr}>
                  <td className="gstr-type">{(gstr.gstr || '').toUpperCase()}</td>
                  <td>{gstr.total_filings}</td>
                  <td>{gstr.filed || 0}</td>
                  <td>{gstr.not_filed || 0}</td>
                  <td>{gstr.payments_received || 0}</td>
                  <td>{gstr.payments_pending || 0}</td>
                  <td>₹{(gstr.total_fees || 0).toLocaleString()}</td>
                  <td className="highlight-success">₹{(gstr.collected_fees || 0).toLocaleString()}</td>
                  <td className="highlight-danger">₹{(gstr.uncollected_fees || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue Analysis */}
      {revenueAnalysis && (
        <div className="revenue-analysis">
          <h2>💵 Revenue Analysis</h2>
          <div className="revenue-cards">
            <div className="revenue-card total">
              <h3>Total Revenue Generated</h3>
              <div className="revenue-amount">
                ₹{(revenueAnalysis.total_revenue || 0).toLocaleString()}
              </div>
            </div>
            <div className="revenue-card received">
              <h3>Revenue Received</h3>
              <div className="revenue-amount">
                ₹{(revenueAnalysis.revenue_by_payment_status?.received || 0).toLocaleString()}
              </div>
            </div>
            <div className="revenue-card pending">
              <h3>Revenue Pending</h3>
              <div className="revenue-amount">
                ₹{(revenueAnalysis.revenue_by_payment_status?.pending || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Traders Summary - SORTED BY UNCOLLECTED FEES */}
      {traderSummary.length > 10 && (
        <div className="all-traders">
          <div className="section-header">
            <h2>📋 All Traders Summary</h2>
            <span className="badge primary">{traderSummary.length} Total</span>
            <span className="badge info">Sorted by Uncollected Fees ↓</span>
          </div>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Trade Name</th>
                  <th>Filings</th>
                  <th>Filed</th>
                  <th>Not Filed</th>
                  <th>Paid</th>
                  <th>Unpaid</th>
                  <th>Total Fees</th>
                  <th>Collected Fees</th>
                  <th className="highlight-column">Uncollected Fees ↓</th>
                </tr>
              </thead>
              <tbody>
                {allTradersSorted.map((trader, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td className="trader-name">{trader.trade_name__trade_name}</td>
                    <td>{trader.total_filings}</td>
                    <td>{trader.filed_count || 0}</td>
                    <td>{trader.not_filed_count || 0}</td>
                    <td>{trader.payments_received || 0}</td>
                    <td>{trader.payments_pending || 0}</td>
                    <td>₹{(trader.total_fees || 0).toLocaleString()}</td>
                    <td className="highlight-success">₹{(trader.collectedFees || 0).toLocaleString()}</td>
                    <td className="highlight-danger highlight-column">
                      <strong>₹{(trader.uncollectedFees || 0).toLocaleString()}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GstSummary;